import React from 'react';
import { NumberDisplay } from '../NumberDisplay';
import '../../inventoryHistoryV2.css';

/**
 * Card component to display individual inventory events
 * Styling is done with Tailwind CSS utility classes
 *
 * @param {string} eventType - The type of inventory event (e.g., 'Received Event', 'Moved Event')
 * @param {string} timestamp - The formatted date/time when the event occurred
 * @param {Array} baseInformation - Array containing the standard fields: Location, SKU/Lot Number, Quantity, Running Total, User, Notes, and Reason Code
 * @param {Array} additionalInformation - Optional array of key-value pairs for additional event details
 */
const InventoryEventCard = ({ eventType, timestamp, baseInformation, additionalInformation = [] }) => {
    // Get appropriate CSS class based on event type
    const getEventTypeClass = () => {
        switch (eventType) {
            case 'Received Event':
                return 'event-received';
            case 'Packout Event':
            case 'Packout Summary':
                return 'event-packout';
            case 'Inventory Adjusted Event':
                return 'event-adjusted';
            case 'Prepped Event':
                return 'event-prepped';
            case 'Moved Event':
                return 'event-moved';
            case 'QC Hold Status Change':
                return 'event-qc';
            case 'Expiration Date Changed':
                return 'event-expiration';
            case 'Cycle Count Event':
                return 'event-cycle-count';
            default:
                return 'event-default';
        }
    };

    // Render the value based on its type
    const renderValue = (item) => {
        if (!item || item.value === null || item.value === undefined) {
            return '';
        }

        if (typeof item.value === 'object' && item.value !== null && item.value.type === 'numberDisplay') {
            return (
                <NumberDisplay
                    value={item.value.value || 0}
                    isChangeValue={item.value.isChangeValue || false}
                    className="inline-block px-1 font-bold text-sm"
                />
            );
        }

        // Handle newlines in text values
        if (typeof item.value === 'string' && item.value.includes('\n')) {
            return item.value.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {line}
                </React.Fragment>
            ));
        }

        return item.value;
    };

    // Define fixed field positions
    const fieldPositions = {
        Location: { col: 3, row: 2 },
        Product: { col: 2, row: 2 }, // Combined position for SKU or Lot
        QuantityTotal: { col: 2, row: 1, colSpan: 2 }, // Combined Quantity and Running Total
        User: { col: 3, row: 1 },
        Notes: { col: 4, row: 1, rowSpan: 2 }, // Spans both rows 1 and 2
        // The Reason Code will be displayed stacked with Event Type in the same cell
        // So it doesn't need a position in fieldPositions
    };

    // Create a mapping of field keys to values
    const fieldMap = {};
    baseInformation.forEach((item) => {
        if (item && item.key) {
            fieldMap[item.key] = item;
        }
    });

    // Handle SKU/Lot Number special case (mutually exclusive)
    const skuItem = fieldMap['SKU'];
    const lotItem = fieldMap['Lot Number'];
    const productItem = skuItem || lotItem;
    const productLabel = skuItem ? 'SKU' : 'Lot Number';

    // Get quantity and running total items
    const quantityItem = fieldMap['Quantity'];
    const runningTotalItem = fieldMap['Running Total'];
    const palletId = fieldMap['Pallet'];

    // Get reason code from baseInformation
    const reasonCodeItem = fieldMap['Reason Code'];

    // Helper to check if an item has a valid value
    const hasValue = (item) => {
        if (!item) return false;
        if (item.value === null || item.value === undefined) return false;
        if (typeof item.value === 'string' && item.value.trim() === '') return false;
        return true;
    };

    // Check if reason code exists and is not empty
    const hasReasonCode = hasValue(reasonCodeItem);

    return (
        <div className="inventory-event-card p-3 border rounded-lg shadow-sm mb-2 bg-white hover:shadow-md transition-shadow duration-200">
            <div className="grid grid-cols-4 gap-2">
                {/* Timestamp in top-left cell */}
                <div className="col-start-1 col-span-1 row-start-1 text-sm font-medium text-gray-600 leading-tight">
                    {timestamp}
                </div>

                {/* Event type in second row, first column */}
                <div className="col-start-1 col-span-1 row-start-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs inline-block ${getEventTypeClass()}`}>
                        {eventType}
                    </span>

                    {hasReasonCode && (
                        <div className="mt-2 text-xs">
                            <span className="text-gray-500 font-semibold">Reason Code:</span>
                            <div className="font-medium text-xs leading-tight">{reasonCodeItem.value}</div>
                        </div>
                    )}
                </div>

                {/* Special handling for combined Quantity and Running Total */}
                {(hasValue(quantityItem) || hasValue(runningTotalItem)) && (
                    <div
                        key="field-quantity-total"
                        className="flex items-center"
                        style={{
                            gridColumn: `${fieldPositions.QuantityTotal.col} / span ${
                                fieldPositions.QuantityTotal.colSpan || 1
                            }`,
                            gridRow: fieldPositions.QuantityTotal.row,
                        }}
                    >
                        {hasValue(quantityItem) && (
                            <>
                                <div className="text-xs text-gray-500 font-semibold mr-1">Quantity:</div>
                                <div className="font-medium leading-tight mr-3">{renderValue(quantityItem)}</div>
                            </>
                        )}
                        {hasValue(runningTotalItem) && (
                            <>
                                <div className="text-xs text-gray-500 font-semibold mr-1">Running Total:</div>
                                <div className="font-medium leading-tight">{renderValue(runningTotalItem)}</div>
                            </>
                        )}
                    </div>
                )}

                {/* Render all other fields in their fixed positions */}
                {Object.entries(fieldPositions).map(([fieldKey, position]) => {
                    // Skip the special cases we handle separately
                    if (fieldKey === 'QuantityTotal') {
                        return null;
                    }

                    // Special handling for product field (SKU or Lot)
                    if (fieldKey === 'Product') {
                        if (!productItem || !hasValue(productItem)) {
                            return null;
                        }

                        return (
                            <div
                                key="field-product"
                                className="flex items-center"
                                style={{
                                    gridColumn: `${position.col} / span ${position.colSpan || 1}`,
                                    gridRow: position.row,
                                }}
                            >
                                <>
                                    <div className="text-xs text-gray-500 font-semibold mr-1">{productLabel}:</div>
                                    <div className="font-medium leading-tight mr-3">{renderValue(productItem)}</div>
                                </>

                                {hasValue(palletId) && (
                                    <>
                                        <div className="text-xs text-gray-500 font-semibold mr-1">Pallet:</div>
                                        <div className="font-medium leading-tight">{renderValue(palletId)}</div>
                                    </>
                                )}
                            </div>
                        );
                    }

                    // Normal fields
                    const item = fieldMap[fieldKey];

                    // Don't render if no value exists or if it's the Reason Code (we handle that separately)
                    if (!item || !hasValue(item) || fieldKey === 'Reason Code') {
                        return null;
                    }

                    const isNotesField = fieldKey === 'Notes';

                    return (
                        <div
                            key={`field-${fieldKey}`}
                            className={`flex ${isNotesField ? 'items-start' : 'items-center'}`}
                            style={{
                                gridColumn: `${position.col} / span ${position.colSpan || 1}`,
                                gridRow: position.row,
                                ...(isNotesField && { gridRowEnd: `span ${position.rowSpan || 1}` }),
                                ...(isNotesField && { alignContent: 'start', height: '100%' }),
                            }}
                        >
                            <div className="text-xs text-gray-500 font-semibold mr-1">{fieldKey}:</div>
                            <div className={`font-medium leading-tight ${isNotesField ? 'text-left w-full' : ''}`}>
                                {renderValue(item)}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Additional information section (if any) */}
            {additionalInformation.length > 0 && additionalInformation.some((item) => hasValue(item)) && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-500 font-semibold mb-1">Additional Details</div>
                    <div className="grid grid-cols-3 gap-2">
                        {additionalInformation
                            .filter((item) => hasValue(item))
                            .map((item, index) => (
                                <div key={`additional-${index}`} className="flex items-center">
                                    <div className="text-xs text-gray-500 mr-1">{item.key}:</div>
                                    <div className="font-medium text-sm leading-tight">{renderValue(item)}</div>
                                </div>
                            ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export { InventoryEventCard };
